import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { apis } from "../../../core/services/params";
import Header from "../Header/Header";
import SideNavbar from "../Navbar/SideNavbar";
import OfferPriceTab from "./OfferPriceTab";

interface NddSupplierItem {
    id: number;
    msn: string;
    moglixSupplierId: string;
    finalSPWithTax: number;
    finalSPWithoutTax: number;
    mrp: number;
    discount: number;
    taxPercentage: number;
    status: boolean;
    outOfStock: boolean;
    inventory: number | null;
    moq: number | null;
    isNddOnly: boolean;
    ndd: boolean;
    city: string;
    updatedBy: string | null;
    createdAt: number;
    updatedAt: number;
}

interface ProductPricingItem {
    id: number;
    msn: string;
    universalTP: number;
    universalSPWithTax: number;
    universalSPWithoutTax: number;
    offerPriceWithTax: number | null;
    offerPriceWithoutTax: number | null;
    finalSPWithTax: number;
    finalSPWithoutTax: number;
    mrp: number;
    hsn: string | null;
    taxPercentage: number;
    supplierId: string;
    offer: boolean | null;
    offerStartDate: number | null;
    offerEndDate: number | null;
    status: boolean;
    outOfStock: boolean;
    availableQuantity: number;
    moq: number;
    incrementUnit: number;
    estimatedDelivery: string;
    country: string;
    discount: number;
    createdBy: string | null;
    createdAt: number;
    modifiedBy: string;
    modifiedAt: number;
    useCompetitivePrice: boolean;
    priceType: string;
}

interface PricingHistoryItem {
    id: number;
    msn: string;
    ruleSet: string | null;
    finalSPWithTax: number;
    oldFinalSPWithTax: number;
    finalSPWithoutTax: number;
    oldFinalSPWithoutTax: number;
    pricingRule: string;
    createdBy: string | null;
    createdAt: number;
}

const ProductNddSupplier = () => {
    const { user } = useSelector((s: any) => s.authStore);

    // Active tab state
    const [activeTab, setActiveTab] = useState<'ndd-supplier' | 'product-pricing' | 'offer-price'>('ndd-supplier');

    // NDD Supplier states
    const [data, setData] = useState<NddSupplierItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [nddOfferUploadLoading, setNddOfferUploadLoading] = useState(false);
    const [selectedNddOfferFile, setSelectedNddOfferFile] = useState<File | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadResult, setUploadResult] = useState<any>(null);
    const [showPricingModal, setShowPricingModal] = useState(false);
    const [pricingHistory, setPricingHistory] = useState<any[]>([]);
    const [pricingLoading, setPricingLoading] = useState(false);
    const [selectedMsn, setSelectedMsn] = useState<string>("");
    const [chartTooltip, setChartTooltip] = useState<{
        show: boolean;
        x: number;
        y: number;
        data: any;
    }>({ show: false, x: 0, y: 0, data: null });
    const [filters, setFilters] = useState({
        msn: "",
        moglixSupplierId: "",
        status: "",
        outOfStock: "",
        isNddOnly: "",
        ndd: "",
        city: ""
    });

    // Product Pricing states
    const [pricingData, setPricingData] = useState<ProductPricingItem[]>([]);
    const [pricingLoading2, setPricingLoading2] = useState(false);
    const [pricingCurrentPage, setPricingCurrentPage] = useState(1);
    const [pricingTotalPages, setPricingTotalPages] = useState(1);
    const [pricingTotalCount, setPricingTotalCount] = useState(0);
    const [pricingPageSize, setPricingPageSize] = useState(10);
    const [pricingFilters, setPricingFilters] = useState({
        msn: "",
        supplierId: "",
        status: "",
        outOfStock: ""
    });

    // Product Pricing Upload States
    const [productPricingUploadLoading, setProductPricingUploadLoading] = useState(false);
    const [selectedProductPricingFile, setSelectedProductPricingFile] = useState<File | null>(null);
    const [productPricingUploadResult, setProductPricingUploadResult] = useState<any>(null);
    const [showProductPricingUploadModal, setShowProductPricingUploadModal] = useState(false);

    // Product Pricing History states
    const [productPricingHistory, setProductPricingHistory] = useState<PricingHistoryItem[]>([]);
    const [productPricingHistoryLoading, setProductPricingHistoryLoading] = useState(false);
    const [showProductPricingHistoryModal, setShowProductPricingHistoryModal] = useState(false);
    const [selectedProductMsn, setSelectedProductMsn] = useState<string>("");

    // Fetch data from API
    const fetchData = async (page = 1, appliedFilters = filters) => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                currentPage: page.toString(),
                pageSize: pageSize.toString(),
                ...(appliedFilters.msn && { msn: appliedFilters.msn }),
                ...(appliedFilters.moglixSupplierId && { moglixSupplierId: appliedFilters.moglixSupplierId }),
                ...(appliedFilters.status && { status: appliedFilters.status }),
                ...(appliedFilters.outOfStock && { outOfStock: appliedFilters.outOfStock }),
                ...(appliedFilters.isNddOnly && { isNddOnly: appliedFilters.isNddOnly }),
                ...(appliedFilters.ndd && { ndd: appliedFilters.ndd }),
                ...(appliedFilters.city && { city: appliedFilters.city })
            });

            const response = await fetch(
                `${apis.FETCH_NDD_SUPPLIER}?${queryParams}`,
                {
                    method: "GET"
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch data");
            }

            const result = await response.json();
            setData(result.data || []);
            setTotalPages(result.totalPage || 1);
            setTotalCount(result.totalCount || 0);
            setPageSize(result.pageSize || 10);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to fetch NDD supplier data");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch product pricing data
    const fetchPricingData = async (page = 1, appliedFilters = pricingFilters) => {
        setPricingLoading2(true);
        try {
            const queryParams = new URLSearchParams({
                currentPage: page.toString(),
                pageSize: pricingPageSize.toString(),
                ...(appliedFilters.msn && { msn: appliedFilters.msn }),
                ...(appliedFilters.supplierId && { supplierId: appliedFilters.supplierId }),
                ...(appliedFilters.status && { status: appliedFilters.status }),
                ...(appliedFilters.outOfStock && { outOfStock: appliedFilters.outOfStock })
            });

            const response = await fetch(
                `${apis.FETCH_PRODUCT_PRICING}?${queryParams}`,
                {
                    method: "GET"
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch product pricing data");
            }

            const result = await response.json();
            setPricingData(result.data || []);
            setPricingTotalPages(result.totalPage || 1);
            setPricingTotalCount(result.totalCount || 0);
            setPricingPageSize(result.pageSize || 10);
        } catch (error) {
            console.error("Error fetching product pricing data:", error);
            toast.error("Failed to fetch product pricing data");
            setPricingData([]);
        } finally {
            setPricingLoading2(false);
        }
    };

    // Upload CSV file
    const handleFileUpload = async () => {
        if (!selectedFile) {
            toast.error("Please select a valid CSV file");
            return;
        }

        setUploadLoading(true);

        // Re-validate file before upload
        const isValid = await validateCSVFile(selectedFile);
        if (!isValid) {
            setUploadLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await fetch(`${apis.UPLOAD_NDD_SUPPLIER}?email=${user.email || 'dev@moglix.com'}`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error("Upload failed");
            }
            toast.success(`File uploaded successfully!`);
            setSelectedFile(null);
            const fileInput = document.getElementById('csvFile') as HTMLInputElement;
            if (fileInput) fileInput.value = '';

            fetchData(currentPage, filters);
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error("Network error occurred during upload. Please check your connection and try again.");
        } finally {
            setUploadLoading(false);
        }
    };

    // Validate CSV file structure
    const validateCSVFile = (file: File): Promise<boolean> => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target?.result as string;
                    const lines = text.split('\n').filter(line => line.trim() !== '');

                    if (lines.length < 2) {
                        toast.error("CSV file must contain at least a header row and one data row");
                        resolve(false);
                        return;
                    }

                    const headers = lines[0].split(',').map(h => h.trim());
                    const requiredHeaders = [
                        'msn', 'moglixSupplierId', 'supplierName', 'city', 'offDay', 'isNddOnly'
                    ];

                    // Check if all required headers are present
                    const missingHeaders = requiredHeaders.filter(header =>
                        !headers.some(h => h.toLowerCase() === header.toLowerCase())
                    );

                    if (missingHeaders.length > 0) {
                        toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
                        resolve(false);
                        return;
                    }

                    // Validate data rows
                    let validationErrors: string[] = [];

                    for (let i = 1; i < Math.min(lines.length, 6); i++) { // Validate first 5 data rows
                        const rowData = lines[i].split(',').map(cell => cell.trim());

                        if (rowData.length !== headers.length) {
                            validationErrors.push(`Row ${i + 1}: Column count mismatch`);
                            continue;
                        }

                        // Validate specific fields
                        const msnIndex = headers.findIndex(h => h.toLowerCase() === 'msn');
                        const supplierIdIndex = headers.findIndex(h => h.toLowerCase() === 'moglixsupplierid');
                        const supplierNameIndex = headers.findIndex(h => h.toLowerCase() === 'suppliername');
                        const cityIndex = headers.findIndex(h => h.toLowerCase() === 'city');
                        const offDayIndex = headers.findIndex(h => h.toLowerCase() === 'offday');
                        const isNddOnlyIndex = headers.findIndex(h => h.toLowerCase() === 'isnddonly');

                        if (msnIndex >= 0 && !rowData[msnIndex]) {
                            validationErrors.push(`Row ${i + 1}: msn is required`);
                        }

                        if (supplierIdIndex >= 0 && !rowData[supplierIdIndex]) {
                            validationErrors.push(`Row ${i + 1}: moglixSupplierId is required`);
                        }

                        if (supplierNameIndex >= 0 && !rowData[supplierNameIndex]) {
                            validationErrors.push(`Row ${i + 1}: supplierName is required`);
                        }

                        if (cityIndex >= 0 && !rowData[cityIndex]) {
                            validationErrors.push(`Row ${i + 1}: city is required`);
                        }

                        if (offDayIndex >= 0 && rowData[offDayIndex] && !['0', '1', '2', '3', '4', '5', '6'].includes(rowData[offDayIndex])) {
                            validationErrors.push(`Row ${i + 1}: offDay must be a number between 0-6 (0=Sunday, 6=Saturday)`);
                        }

                        if (isNddOnlyIndex >= 0 && !['true', 'false'].includes(rowData[isNddOnlyIndex].toLowerCase())) {
                            validationErrors.push(`Row ${i + 1}: isNddOnly must be true or false`);
                        }
                    }

                    if (validationErrors.length > 0) {
                        toast.error(`Validation errors found:\n${validationErrors.slice(0, 3).join('\n')}${validationErrors.length > 3 ? '\n...and more' : ''}`);
                        resolve(false);
                        return;
                    }

                    toast.success("CSV file validation passed");
                    resolve(true);
                } catch (error) {
                    toast.error("Error reading CSV file. Please check the file format.");
                    resolve(false);
                }
            };
            reader.readAsText(file);
        });
    };

    // Validate NDD Offer Price CSV file structure
    const validateNddOfferCSVFile = (file: File): Promise<boolean> => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target?.result as string;
                    const lines = text.split('\n').filter(line => line.trim() !== '');

                    if (lines.length < 2) {
                        toast.error("CSV file must contain at least a header row and one data row");
                        resolve(false);
                        return;
                    }

                    const headers = lines[0].split(',').map(h => h.trim());
                    const requiredHeaders = [
                        'Id Product', 'Offer Price', 'In Offer', 'Supplier Id'
                    ];

                    // Check if all required headers are present
                    const missingHeaders = requiredHeaders.filter(header =>
                        !headers.some(h => h.toLowerCase() === header.toLowerCase())
                    );

                    if (missingHeaders.length > 0) {
                        toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
                        resolve(false);
                        return;
                    }

                    // Validate data rows
                    let validationErrors: string[] = [];

                    for (let i = 1; i < Math.min(lines.length, 6); i++) { // Validate first 5 data rows
                        const rowData = lines[i].split(',').map(cell => cell.trim());

                        if (rowData.length !== headers.length) {
                            validationErrors.push(`Row ${i + 1}: Column count mismatch`);
                            continue;
                        }

                        // Validate specific fields
                        const idProductIndex = headers.findIndex(h => h.toLowerCase() === 'id product');
                        const offerPriceIndex = headers.findIndex(h => h.toLowerCase() === 'offer price');
                        const inOfferIndex = headers.findIndex(h => h.toLowerCase() === 'in offer');
                        const supplierIdIndex = headers.findIndex(h => h.toLowerCase() === 'supplier id');

                        if (idProductIndex >= 0 && !rowData[idProductIndex]) {
                            validationErrors.push(`Row ${i + 1}: Id Product is required`);
                        }

                        if (offerPriceIndex >= 0 && rowData[offerPriceIndex] && isNaN(Number(rowData[offerPriceIndex]))) {
                            validationErrors.push(`Row ${i + 1}: Offer Price must be a valid number`);
                        }

                        if (inOfferIndex >= 0 && !['yes', 'no', 'true', 'false'].includes(rowData[inOfferIndex].toLowerCase())) {
                            validationErrors.push(`Row ${i + 1}: In Offer must be Yes/No or true/false`);
                        }

                        if (supplierIdIndex >= 0 && !rowData[supplierIdIndex]) {
                            validationErrors.push(`Row ${i + 1}: Supplier Id is required`);
                        }
                    }

                    if (validationErrors.length > 0) {
                        toast.error(`Validation errors found:\n${validationErrors.slice(0, 3).join('\n')}${validationErrors.length > 3 ? '\n...and more' : ''}`);
                        resolve(false);
                        return;
                    }

                    toast.success("NDD Offer Price CSV file validation passed");
                    resolve(true);
                } catch (error) {
                    toast.error("Error reading CSV file. Please check the file format.");
                    resolve(false);
                }
            };
            reader.readAsText(file);
        });
    };

    // Handle file selection
    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.name.toLowerCase().endsWith('.csv')) {
                toast.error("Please select a valid CSV file");
                return;
            }

            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                toast.error("File size must be less than 10MB");
                return;
            }

            const isValid = await validateCSVFile(file);
            if (isValid) {
                setSelectedFile(file);
            } else {
                // Clear the file input
                event.target.value = '';
                setSelectedFile(null);
            }
        }
    };

    // Handle NDD Offer Price file selection
    const handleNddOfferFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.name.toLowerCase().endsWith('.csv')) {
                toast.error("Please select a valid CSV file");
                return;
            }

            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                toast.error("File size must be less than 10MB");
                return;
            }

            const isValid = await validateNddOfferCSVFile(file);
            if (isValid) {
                setSelectedNddOfferFile(file);
            } else {
                // Clear the file input
                event.target.value = '';
                setSelectedNddOfferFile(null);
            }
        }
    };

    // Handle NDD Offer Price file upload
    const handleNddOfferFileUpload = async () => {
        if (!selectedNddOfferFile) {
            toast.error("Please select a file first");
            return;
        }

        setNddOfferUploadLoading(true);

        // Re-validate file before upload
        const isValid = await validateNddOfferCSVFile(selectedNddOfferFile);
        if (!isValid) {
            setNddOfferUploadLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedNddOfferFile);
        formData.append('request', JSON.stringify({
            uploadedBy: user?.email || "admin@moglix.com",
            uploadedFor: "create",
            uploadFilePath: "",
            uploadFileName: ""
        }));

        try {
            const response = await fetch(`${apis.UPLOAD_NDD_OFFER_PRICE}`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const result = await response.json();

                // Show detailed result in modal
                setUploadResult(result);
                setShowUploadModal(true);

                setSelectedNddOfferFile(null);

                // Reset file input
                const fileInput = document.getElementById('nddOfferCsvFile') as HTMLInputElement;
                if (fileInput) {
                    fileInput.value = '';
                }

                // Refresh data after successful upload
                await fetchData(currentPage, filters);
            } else {
                const errorData = await response.json();
                const errorMessage = errorData.message || "Upload failed";

                // Show detailed error information
                if (errorData.errors && Array.isArray(errorData.errors)) {
                    const errorDetails = errorData.errors.slice(0, 3).map((err: any, index: number) =>
                        `Row ${err.row || index + 1}: ${err.message || err}`
                    ).join('\n');
                    toast.error(`${errorMessage}\n${errorDetails}${errorData.errors.length > 3 ? '\n...and more errors' : ''}`);
                } else {
                    toast.error(errorMessage);
                }
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error("Network error occurred during upload. Please check your connection and try again.");
        } finally {
            setNddOfferUploadLoading(false);
        }
    };

    // Handle Product Pricing file select
    const handleProductPricingFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.name.toLowerCase().endsWith('.csv')) {
                toast.error("Please select a valid CSV file");
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                toast.error("File size must be less than 10MB");
                return;
            }
            setSelectedProductPricingFile(file);
        }
    };

    // Handle Product Pricing file upload
    const handleProductPricingFileUpload = async () => {
        if (!selectedProductPricingFile) {
            toast.error("Please select a file first");
            return;
        }

        setProductPricingUploadLoading(true);

        const formData = new FormData();
        formData.append('file', selectedProductPricingFile);
        formData.append('request', JSON.stringify({
            uploadedBy: user?.email || "admin@moglix.com",
            uploadedFor: "create",
            uploadFilePath: "",
            uploadFileName: ""
        }));

        try {
            const response = await fetch(`${apis.UPLOAD_PRODUCT_PRICING}`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const result = await response.json();
                setProductPricingUploadResult(result.data || {});
                setShowProductPricingUploadModal(true);
                setSelectedProductPricingFile(null);

                const fileInput = document.getElementById('productPricingCsvFile') as HTMLInputElement;
                if (fileInput) {
                    fileInput.value = '';
                }

                await fetchPricingData(pricingCurrentPage, pricingFilters);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || "Upload failed");
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error("Network error occurred during upload. Please check your connection and try again.");
        } finally {
            setProductPricingUploadLoading(false);
        }
    };

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchData(page, filters);
    };

    // Handle filter change for text inputs (only update state, don't trigger API)
    const handleFilterChange = (field: string, value: string) => {
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);
    };

    // Handle filter change for dropdowns (trigger API immediately)
    const handleDropdownFilterChange = (field: string, value: string) => {
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);
        setCurrentPage(1);
        fetchData(1, newFilters);
    };

    // Handle filter apply (trigger API call)
    const applyFilters = () => {
        setCurrentPage(1);
        fetchData(1, filters);
    };

    // Handle filter key press (apply on Enter)
    const handleFilterKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            applyFilters();
        }
    };

    // Clear all filters
    const clearProductCache = async (productId: string) => {
        if (!productId) return;
        const loadingToast = toast.loading(`Clearing cache for ${productId}...`);
        try {
            const apiUrl = `${process.env.REACT_APP_CMS_SERVICE}/api/platform/purge-cache/product?productIds=${encodeURIComponent(productId)}&userId=${encodeURIComponent(user.id || -1)}`;
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            toast.success(`Product cache cleared successfully for: ${productId}`, { id: loadingToast });
        } catch (err) {
            console.error('Product cache clear error:', err);
            toast.error("Failed to clear product cache!", { id: loadingToast });
        }
    };

    const clearFilters = () => {
        const emptyFilters = {
            msn: "",
            moglixSupplierId: "",
            status: "",
            outOfStock: "",
            isNddOnly: "",
            ndd: "",
            city: ""
        };
        setFilters(emptyFilters);
        setCurrentPage(1);
        fetchData(1, emptyFilters);
    };

    // Download sample CSV template
    const downloadTemplate = () => {
        const csvHeaders = "msn,moglixSupplierId,supplierName,city,offDay,isNddOnly";
        const sampleData = "MSN123456789,MS123456,MANIKA ORDER SUPPLIER,Kolkata,6,FALSE";
        const csvContent = `${csvHeaders}\n${sampleData}`;

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'ndd_supplier_template.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    // Download NDD Offer Price CSV template
    const downloadNddOfferTemplate = () => {
        const csvHeaders = "Id Product,Offer Price,In Offer,Supplier Id";
        const sampleData = "MSN1200W24M292,8294,Yes,MS8574F4";
        const csvContent = `${csvHeaders}\n${sampleData}`;

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'ndd_offer_price_template.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    // Download Product Pricing template
    const downloadProductPricingTemplate = () => {
        const csvHeaders = "Id Product,Offer Price,In Offer";
        const sampleData = "MSN12345678,1600,Yes";
        const csvContent = `${csvHeaders}\n${sampleData}`;

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'product_pricing_template.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    // Fetch pricing history
    const fetchPricingHistory = async (msn: string, moglixSupplierId: string) => {
        setPricingLoading(true);
        setPricingHistory([]);
        setSelectedMsn(msn);

        try {
            const response = await fetch(`${apis.FETCH_NDD_SUPPLIER_PRICE_HISTORY}?msn=${msn}&moglixSupplierId=${moglixSupplierId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const result = await response.json();
                // Parse the data to ensure numbers are properly formatted
                const parsedData = (result.data || []).map((item: any) => {
                    const parsed = {
                        ...item,
                        finalSpWithTax: parseFloat(item.finalSpWithTax) || 0,
                        oldFinalSpWithTax: parseFloat(item.oldFinalSpWithTax) || 0,
                        finalSpWithoutTax: parseFloat(item.finalSpWithoutTax) || 0,
                        oldFinalSpWithoutTax: parseFloat(item.oldFinalSpWithoutTax) || 0
                    };
                    return parsed;
                });

                setPricingHistory(parsedData);
                setShowPricingModal(true);
            } else {
                toast.error("Failed to fetch pricing history");
            }
        } catch (error) {
            console.error('Error fetching pricing history:', error);
            toast.error("Failed to fetch pricing history");
        } finally {
            setPricingLoading(false);
        }
    };

    // Fetch product pricing history
    const fetchProductPricingHistory = async (msn: string) => {
        setProductPricingHistoryLoading(true);
        setProductPricingHistory([]);
        setSelectedProductMsn(msn);

        try {
            console.log('🏷️ Fetching product pricing history for MSN:', msn);
            const response = await fetch(`${apis.FETCH_PRODUCT_PRICING_HISTORY}?msn=${msn}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`
                }
            });

            const result = await response.json();

            if (result.status === true && result.data) {
                // Parse the data to ensure numbers are properly formatted
                const parsedData = (result.data || []).map((item: any) => {
                    const parsed = {
                        ...item,
                        finalSPWithTax: parseFloat(item.finalSPWithTax) || 0,
                        oldFinalSPWithTax: parseFloat(item.oldFinalSPWithTax) || 0,
                        finalSPWithoutTax: parseFloat(item.finalSPWithoutTax) || 0,
                        oldFinalSPWithoutTax: parseFloat(item.oldFinalSPWithoutTax) || 0
                    };
                    return parsed;
                });

                setProductPricingHistory(parsedData);
                setShowProductPricingHistoryModal(true);
            } else {
                toast.error("Failed to fetch product pricing history");
            }
        } catch (error) {
            console.error('Error fetching product pricing history:', error);
            toast.error("Failed to fetch product pricing history");
        } finally {
            setProductPricingHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'ndd-supplier') {
            fetchData();
        } else if (activeTab === 'product-pricing') {
            fetchPricingData();
        }
    }, [activeTab]);

    // Generate pagination numbers
    const getPaginationNumbers = () => {
        const numbers = [];
        const maxVisible = 10;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            numbers.push(i);
        }
        return numbers;
    };

    // Generate pagination numbers for product pricing
    const getPricingPaginationNumbers = () => {
        const numbers = [];
        const maxVisible = 10;
        let start = Math.max(1, pricingCurrentPage - Math.floor(maxVisible / 2));
        let end = Math.min(pricingTotalPages, start + maxVisible - 1);

        if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            numbers.push(i);
        }
        return numbers;
    };

    // Handle pagination for product pricing
    const handlePricingPageChange = (page: number) => {
        setPricingCurrentPage(page);
        fetchPricingData(page);
    };

    // Handle filters for product pricing
    const handlePricingFilters = () => {
        setPricingCurrentPage(1);
        fetchPricingData(1, pricingFilters);
    };

    const handlePricingFilterChange = (key: string, value: string) => {
        setPricingFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handlePricingDropdownFilterChange = (field: string, value: string) => {
        const newFilters = { ...pricingFilters, [field]: value };
        setPricingFilters(newFilters);
        setPricingCurrentPage(1);
        fetchPricingData(1, newFilters);
    };

    const handlePricingFilterKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handlePricingFilters();
        }
    };

    const clearPricingFilters = () => {
        const resetFilters = {
            msn: "",
            supplierId: "",
            status: "",
            outOfStock: ""
        };
        setPricingFilters(resetFilters);
        setPricingCurrentPage(1);
        fetchPricingData(1, resetFilters);
    };

    return (
        <div className="flex mainWrapper w-full h-full">
            <div className="sidenav-wrapper">
                <SideNavbar />
            </div>
            <div className="right-container">
                <div className="header-container">
                    <Header />
                </div>
                <div className="body-container">
                    <div className="min-h-screen bg-gray-50">
                        {/* Header */}
                        <div className="bg-white border-b border-gray-200">
                            <div className="px-6 py-4">
                                <div className="flex justify-between items-center">
                                    <h1 className="text-2xl font-bold text-gray-900">
                                        {activeTab === 'ndd-supplier' ? 'PRODUCT NDD SUPPLIER' : activeTab === 'product-pricing' ? 'PRODUCT PRICING' : 'FLASH DEAL'}
                                    </h1>
                                    {((activeTab === 'ndd-supplier' && totalCount > 0) ||
                                        (activeTab === 'product-pricing' && pricingTotalCount > 0)) && (
                                            <div className="text-sm text-gray-600">
                                                {activeTab === 'ndd-supplier'
                                                    ? `Total Records: ${totalCount.toLocaleString()} | Page ${currentPage} of ${totalPages}`
                                                    : `Total Records: ${pricingTotalCount.toLocaleString()} | Page ${pricingCurrentPage} of ${pricingTotalPages}`
                                                }
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="bg-white border-b border-gray-200">
                            <div className="px-6">
                                <nav className="flex space-x-8">
                                    <button
                                        onClick={() => setActiveTab('ndd-supplier')}
                                        className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'ndd-supplier'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`}
                                    >
                                        NDD Pricing
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('product-pricing')}
                                        className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'product-pricing'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`}
                                    >
                                        Product Pricing
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('offer-price')}
                                        className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'offer-price'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`}
                                    >
                                        Flash Deal
                                    </button>
                                </nav>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="p-6">
                            {activeTab === 'ndd-supplier' && (
                                <>
                                    {/* Upload Section */}
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {/* NDD Supplier Upload */}
                                            <div className="border border-gray-200 rounded-lg p-4">
                                                <h3 className="text-lg font-semibold text-gray-900 mb-4">NDD Supplier Data</h3>
                                                <div className="flex flex-wrap items-center gap-4">
                                                    <div className="flex items-center">
                                                        <input
                                                            type="file"
                                                            id="csvFile"
                                                            accept=".csv"
                                                            onChange={handleFileSelect}
                                                            className="hidden"
                                                        />
                                                        <label
                                                            htmlFor="csvFile"
                                                            className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                        >
                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                            </svg>
                                                            Choose File
                                                        </label>
                                                        {selectedFile && (
                                                            <span className="ml-3 text-sm text-gray-600 truncate max-w-[200px]" title={selectedFile.name}>
                                                                {selectedFile.name}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <button
                                                        onClick={downloadTemplate}
                                                        className="inline-flex items-center px-4 py-2 border border-green-300 text-green-700 rounded-md hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium"
                                                    >
                                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        Download Template
                                                    </button>

                                                    <button
                                                        onClick={handleFileUpload}
                                                        disabled={!selectedFile || uploadLoading}
                                                        className="inline-flex items-center px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {uploadLoading ? (
                                                            <>
                                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
                                                                    <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75"></path>
                                                                </svg>
                                                                Uploading...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                                </svg>
                                                                Upload Supplier Data
                                                            </>
                                                        )}
                                                    </button>

                                                </div>
                                            </div>

                                            {/* NDD Offer Price Upload */}
                                            <div className="border border-gray-200 rounded-lg p-4">
                                                <h3 className="text-lg font-semibold text-gray-900 mb-4">NDD Offer Price</h3>
                                                <div className="flex flex-wrap items-center gap-4">
                                                    <div className="flex items-center">
                                                        <input
                                                            type="file"
                                                            id="nddOfferCsvFile"
                                                            accept=".csv"
                                                            onChange={handleNddOfferFileSelect}
                                                            className="hidden"
                                                        />
                                                        <label
                                                            htmlFor="nddOfferCsvFile"
                                                            className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                                        >
                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                            </svg>
                                                            Choose File
                                                        </label>
                                                        {selectedNddOfferFile && (
                                                            <span className="ml-3 text-sm text-gray-600 truncate max-w-[200px]" title={selectedNddOfferFile.name}>
                                                                {selectedNddOfferFile.name}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <button
                                                        onClick={downloadNddOfferTemplate}
                                                        className="inline-flex items-center px-4 py-2 border border-purple-300 text-purple-700 rounded-md hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm font-medium"
                                                    >
                                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        Download Template
                                                    </button>

                                                    <button
                                                        onClick={handleNddOfferFileUpload}
                                                        disabled={!selectedNddOfferFile || nddOfferUploadLoading}
                                                        className="inline-flex items-center px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {nddOfferUploadLoading ? (
                                                            <>
                                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
                                                                    <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75"></path>
                                                                </svg>
                                                                Uploading...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                                </svg>
                                                                Upload Offer Price
                                                            </>
                                                        )}
                                                    </button>

                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Data Table */}
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                        {/* Filters Section */}
                                        <div className="p-4 border-b border-gray-200 bg-gray-50">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 items-end">
                                                {/* MSN Filter */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">MSN</label>
                                                    <input
                                                        type="text"
                                                        value={filters.msn}
                                                        onChange={(e) => handleFilterChange('msn', e.target.value)}
                                                        onKeyPress={handleFilterKeyPress}
                                                        placeholder="Filter by MSN"
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* Supplier ID Filter */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Supplier ID</label>
                                                    <input
                                                        type="text"
                                                        value={filters.moglixSupplierId}
                                                        onChange={(e) => handleFilterChange('moglixSupplierId', e.target.value)}
                                                        onKeyPress={handleFilterKeyPress}
                                                        placeholder="Filter by Supplier ID"
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* Status Filter */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                                                    <select
                                                        value={filters.status}
                                                        onChange={(e) => handleDropdownFilterChange('status', e.target.value)}
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="">All Status</option>
                                                        <option value="true">Active</option>
                                                        <option value="false">Inactive</option>
                                                    </select>
                                                </div>

                                                {/* Out of Stock Filter */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Stock Status</label>
                                                    <select
                                                        value={filters.outOfStock}
                                                        onChange={(e) => handleDropdownFilterChange('outOfStock', e.target.value)}
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="">All Stock</option>
                                                        <option value="false">In Stock</option>
                                                        <option value="true">Out of Stock</option>
                                                    </select>
                                                </div>

                                                {/* Is NDD Only Filter */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">NDD Only</label>
                                                    <select
                                                        value={filters.isNddOnly}
                                                        onChange={(e) => handleDropdownFilterChange('isNddOnly', e.target.value)}
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="">All</option>
                                                        <option value="true">NDD Only</option>
                                                        <option value="false">Not NDD Only</option>
                                                    </select>
                                                </div>

                                                {/* NDD Filter */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">NDD Available</label>
                                                    <select
                                                        value={filters.ndd}
                                                        onChange={(e) => handleDropdownFilterChange('ndd', e.target.value)}
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="">All</option>
                                                        <option value="true">NDD Available</option>
                                                        <option value="false">NDD Not Available</option>
                                                    </select>
                                                </div>

                                                {/* City Filter */}
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                                                    <input
                                                        type="text"
                                                        value={filters.city}
                                                        onChange={(e) => handleFilterChange('city', e.target.value)}
                                                        onKeyPress={handleFilterKeyPress}
                                                        placeholder="Filter by City"
                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* Action Buttons */}
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={applyFilters}
                                                        className="flex-1 text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 font-medium h-[34px] shadow-sm"
                                                    >
                                                        Apply Filters
                                                    </button>
                                                    <button
                                                        onClick={clearFilters}
                                                        className="flex-1 text-sm text-blue-600 bg-white border border-blue-600 px-3 py-1.5 rounded hover:bg-blue-50 font-medium h-[34px] shadow-sm"
                                                    >
                                                        Clear All
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Table */}
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-800 text-white">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">
                                                            MSN
                                                        </th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">
                                                            Supplier ID
                                                        </th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">
                                                            Final SP (Tax)
                                                        </th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">
                                                            MRP
                                                        </th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">
                                                            Stock Status
                                                        </th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">
                                                            NDD Only
                                                        </th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">
                                                            NDD
                                                        </th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">
                                                            City
                                                        </th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">
                                                            Status
                                                        </th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">
                                                            Updated At
                                                        </th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">
                                                            Action
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {loading ? (
                                                        <tr>
                                                            <td colSpan={11} className="px-6 py-4 text-center">
                                                                <div className="flex justify-center items-center">
                                                                    <svg className="animate-spin h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
                                                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
                                                                        <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75"></path>
                                                                    </svg>
                                                                    <span className="ml-2">Loading...</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ) : data.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={12} className="px-6 py-8 text-center text-gray-500">
                                                                No data found
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        data.map((item, index) => (
                                                            <tr key={item.id || index} className="hover:bg-gray-50">
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                                    {item.msn}
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                                    {item.moglixSupplierId}
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                                    ₹{item.finalSPWithTax?.toFixed(2) || '0.00'}
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                                    ₹{item.mrp?.toFixed(2) || '0.00'}
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap">
                                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${!item.outOfStock
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : 'bg-red-100 text-red-800'
                                                                        }`}>
                                                                        {!item.outOfStock ? 'In Stock' : 'Out of Stock'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap">
                                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isNddOnly
                                                                        ? 'bg-blue-100 text-blue-800'
                                                                        : 'bg-gray-100 text-gray-800'
                                                                        }`}>
                                                                        {item.isNddOnly ? 'NDD Only' : 'Regular'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap">
                                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.ndd
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : 'bg-red-100 text-red-800'
                                                                        }`}>
                                                                        {item.ndd ? 'Yes' : 'No'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                                    {item.city}
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap">
                                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.status
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : 'bg-red-100 text-red-800'
                                                                        }`}>
                                                                        {item.status ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                                    {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '-'}
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs">
                                                                    <div className="relative group">
                                                                        <button
                                                                            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                                                                            onClick={() => fetchPricingHistory(item.msn, item.moglixSupplierId)}
                                                                            disabled={pricingLoading}
                                                                            title="View Actions"
                                                                        >
                                                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                                                            </svg>
                                                                        </button>
                                                                        <div className="absolute right-0 top-6 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 z-10 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg">
                                                                            <button
                                                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none flex items-center"
                                                                                onClick={() => fetchPricingHistory(item.msn, item.moglixSupplierId)}
                                                                                disabled={pricingLoading}
                                                                            >
                                                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                                                </svg>
                                                                                Pricing History
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination */}
                                        {totalPages > 1 && (
                                            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                                                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                                    <div className="text-sm text-gray-700">
                                                        Displaying {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount.toLocaleString()} results
                                                    </div>

                                                    {/* Go to page */}
                                                    <div className="flex items-center space-x-2">
                                                        <label className="text-sm text-gray-700">Go to page:</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={totalPages}
                                                            value={currentPage}
                                                            onChange={(e) => {
                                                                const page = parseInt(e.target.value);
                                                                if (page >= 1 && page <= totalPages) {
                                                                    handlePageChange(page);
                                                                }
                                                            }}
                                                            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            disabled={loading}
                                                        />
                                                        <span className="text-sm text-gray-700">of {totalPages}</span>
                                                    </div>
                                                    <div className="flex items-center space-x-1">
                                                        {/* First page button */}
                                                        {currentPage > 1 && (
                                                            <button
                                                                onClick={() => handlePageChange(1)}
                                                                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                                                                disabled={loading}
                                                            >
                                                                ««
                                                            </button>
                                                        )}

                                                        {/* Previous button */}
                                                        {currentPage > 1 && (
                                                            <button
                                                                onClick={() => handlePageChange(currentPage - 1)}
                                                                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                                                                disabled={loading}
                                                            >
                                                                Previous
                                                            </button>
                                                        )}

                                                        {/* Page numbers */}
                                                        {getPaginationNumbers().map((pageNum) => (
                                                            <button
                                                                key={pageNum}
                                                                onClick={() => handlePageChange(pageNum)}
                                                                disabled={loading}
                                                                className={`px-3 py-2 text-sm border rounded ${currentPage === pageNum
                                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                                    : 'border-gray-300 hover:bg-gray-50'
                                                                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        ))}

                                                        {/* Next button */}
                                                        {currentPage < totalPages && (
                                                            <button
                                                                onClick={() => handlePageChange(currentPage + 1)}
                                                                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                                                                disabled={loading}
                                                            >
                                                                Next
                                                            </button>
                                                        )}

                                                        {/* Last page button */}
                                                        {currentPage < totalPages && (
                                                            <button
                                                                onClick={() => handlePageChange(totalPages)}
                                                                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                                                                disabled={loading}
                                                            >
                                                                »»
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Product Pricing Tab Content */}
                            {activeTab === 'product-pricing' && (
                                <>
                                    {/* Upload Section for Product Pricing */}
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                                        <div className="border border-gray-200 rounded-lg p-4">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Upload Product Pricing</h3>
                                            <div className="flex flex-wrap items-center gap-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="file"
                                                        id="productPricingCsvFile"
                                                        accept=".csv"
                                                        onChange={handleProductPricingFileSelect}
                                                        className="hidden"
                                                    />
                                                    <label
                                                        htmlFor="productPricingCsvFile"
                                                        className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                    >
                                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        Choose File
                                                    </label>
                                                    {selectedProductPricingFile && (
                                                        <span className="ml-3 text-sm text-gray-600">
                                                            {selectedProductPricingFile.name}
                                                        </span>
                                                    )}
                                                </div>

                                                <button
                                                    onClick={downloadProductPricingTemplate}
                                                    className="inline-flex items-center px-4 py-2 border border-green-300 text-green-700 rounded-md hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium"
                                                >
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    Download Template
                                                </button>

                                                <button
                                                    onClick={handleProductPricingFileUpload}
                                                    disabled={!selectedProductPricingFile || productPricingUploadLoading}
                                                    className="inline-flex items-center px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {productPricingUploadLoading ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
                                                                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75"></path>
                                                            </svg>
                                                            Uploading...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                            </svg>
                                                            Upload Product Pricing
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Filters Section */}
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 py-4 px-6">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 items-end">
                                            {/* MSN Filter */}
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">MSN</label>
                                                <input
                                                    type="text"
                                                    value={pricingFilters.msn}
                                                    onChange={(e) => handlePricingFilterChange('msn', e.target.value)}
                                                    onKeyPress={handlePricingFilterKeyPress}
                                                    placeholder="Filter by MSN"
                                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>

                                            {/* Supplier ID Filter */}
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Supplier ID</label>
                                                <input
                                                    type="text"
                                                    value={pricingFilters.supplierId}
                                                    onChange={(e) => handlePricingFilterChange('supplierId', e.target.value)}
                                                    onKeyPress={handlePricingFilterKeyPress}
                                                    placeholder="Filter by Supplier ID"
                                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>

                                            {/* Status Filter */}
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                                                <select
                                                    value={pricingFilters.status}
                                                    onChange={(e) => handlePricingDropdownFilterChange('status', e.target.value)}
                                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                >
                                                    <option value="">All Status</option>
                                                    <option value="true">Active</option>
                                                    <option value="false">Inactive</option>
                                                </select>
                                            </div>

                                            {/* Out of Stock Filter */}
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Stock Status</label>
                                                <select
                                                    value={pricingFilters.outOfStock}
                                                    onChange={(e) => handlePricingDropdownFilterChange('outOfStock', e.target.value)}
                                                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                >
                                                    <option value="">All Stock</option>
                                                    <option value="false">In Stock</option>
                                                    <option value="true">Out of Stock</option>
                                                </select>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={handlePricingFilters}
                                                    className="flex-1 text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 font-medium h-[34px] shadow-sm"
                                                >
                                                    Apply Filters
                                                </button>
                                                <button
                                                    onClick={clearPricingFilters}
                                                    className="flex-1 text-sm text-blue-600 bg-white border border-blue-600 px-3 py-1.5 rounded hover:bg-blue-50 font-medium h-[34px] shadow-sm"
                                                >
                                                    Clear All
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Product Pricing Table */}
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-800 text-white">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">MSN</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Supplier ID</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Final SP (Tax)</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Final SP (No Tax)</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">MRP</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Offer Price (Tax)</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Offer Price (No Tax)</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Tax %</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">HSN</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Available Qty</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">MOQ</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Stock Status</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Status</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Modified At</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {pricingLoading2 ? (
                                                        <tr>
                                                            <td colSpan={15} className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                                                                <div className="flex justify-center items-center">
                                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                                                    <span className="ml-2">Loading product pricing data...</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ) : pricingData.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={15} className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                                                                No product pricing data found
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        pricingData.map((item) => (
                                                            <tr key={item.id} className="hover:bg-gray-50">
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                                    {/* @@ AI-COMMENT: Clickable MSN to clear product cache */}
                                                                    <button
                                                                        onClick={() => clearProductCache(item.msn)}
                                                                        className="text-blue-600 hover:text-blue-800 hover:underline focus:outline-none"
                                                                        title="Click to clear product cache"
                                                                    >
                                                                        {item.msn || 'N/A'}
                                                                    </button>
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{item.supplierId || 'N/A'}</td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">₹{item.finalSPWithTax ? Number(item.finalSPWithTax).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}</td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">₹{item.finalSPWithoutTax ? Number(item.finalSPWithoutTax).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}</td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">₹{item.mrp ? Number(item.mrp).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}</td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">₹{item.offerPriceWithTax ? Number(item.offerPriceWithTax).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}</td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">₹{item.offerPriceWithoutTax ? Number(item.offerPriceWithoutTax).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}</td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{item.taxPercentage ? `${Number(item.taxPercentage).toFixed(2)}%` : 'N/A'}</td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{item.hsn || 'N/A'}</td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{item.availableQuantity || 'N/A'}</td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{item.moq || 'N/A'}</td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs">
                                                                    <span className={`inline-flex px-2 py-1 text-[11px] font-semibold rounded-md ${item.outOfStock ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                                                        }`}>
                                                                        {item.outOfStock ? 'Out of Stock' : 'In Stock'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs">
                                                                    <span className={`inline-flex px-2 py-1 text-[11px] font-semibold rounded-md ${item.status ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                                                        }`}>
                                                                        {item.status ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{item.modifiedAt ? new Date(item.modifiedAt).toLocaleString() : 'N/A'}</td>
                                                                <td className="px-3 py-2 whitespace-nowrap text-xs">
                                                                    <div className="relative group">
                                                                        <button
                                                                            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                                                                            onClick={() => fetchProductPricingHistory(item.msn)}
                                                                            disabled={productPricingHistoryLoading}
                                                                            title="View Actions"
                                                                        >
                                                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                                                            </svg>
                                                                        </button>
                                                                        <div className="absolute right-0 top-6 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 z-10 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg">
                                                                            <button
                                                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none flex items-center"
                                                                                onClick={() => fetchProductPricingHistory(item.msn)}
                                                                                disabled={productPricingHistoryLoading}
                                                                            >
                                                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                                                </svg>
                                                                                Pricing History
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Product Pricing Pagination */}
                                        {!pricingLoading2 && pricingData.length > 0 && (
                                            <div className="px-6 py-4 border-t border-gray-200">
                                                <div className="flex justify-between items-center">
                                                    <div className="text-sm text-gray-700">
                                                        Showing page {pricingCurrentPage} of {pricingTotalPages} ({pricingTotalCount.toLocaleString()} total records)
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        {/* First page button */}
                                                        {pricingCurrentPage > 1 && (
                                                            <button
                                                                onClick={() => handlePricingPageChange(1)}
                                                                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                                                                disabled={pricingLoading2}
                                                            >
                                                                ««
                                                            </button>
                                                        )}

                                                        {/* Previous page button */}
                                                        {pricingCurrentPage > 1 && (
                                                            <button
                                                                onClick={() => handlePricingPageChange(pricingCurrentPage - 1)}
                                                                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                                                                disabled={pricingLoading2}
                                                            >
                                                                ‹ Previous
                                                            </button>
                                                        )}

                                                        {/* Page numbers */}
                                                        {getPricingPaginationNumbers().map(pageNum => (
                                                            <button
                                                                key={pageNum}
                                                                onClick={() => handlePricingPageChange(pageNum)}
                                                                className={`px-3 py-2 text-sm border rounded ${pageNum === pricingCurrentPage
                                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                                    : 'border-gray-300 hover:bg-gray-50'
                                                                    }`}
                                                                disabled={pricingLoading2}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        ))}

                                                        {/* Next page button */}
                                                        {pricingCurrentPage < pricingTotalPages && (
                                                            <button
                                                                onClick={() => handlePricingPageChange(pricingCurrentPage + 1)}
                                                                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                                                                disabled={pricingLoading2}
                                                            >
                                                                Next ›
                                                            </button>
                                                        )}

                                                        {/* Last page button */}
                                                        {pricingCurrentPage < pricingTotalPages && (
                                                            <button
                                                                onClick={() => handlePricingPageChange(pricingTotalPages)}
                                                                className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                                                                disabled={pricingLoading2}
                                                            >
                                                                »»
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                            {activeTab === 'offer-price' && <OfferPriceTab user={user} />}
                        </div>

                        {/* Pricing History Modal */}
                        {showPricingModal && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                                <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
                                    <div className="mt-3">
                                        {/* Modal Header */}
                                        <div className="flex items-center justify-between pb-4 border-b">
                                            <h3 className="text-lg font-bold text-gray-900">
                                                Pricing History - {selectedMsn}
                                            </h3>
                                            <button
                                                onClick={() => setShowPricingModal(false)}
                                                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                            >
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>

                                        {pricingLoading ? (
                                            <div className="flex justify-center items-center py-8">
                                                <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
                                                    <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75"></path>
                                                </svg>
                                                <span className="ml-2">Loading pricing history...</span>
                                            </div>
                                        ) : (
                                            <div className="mt-6 space-y-6 max-h-[70vh] overflow-y-auto">
                                                {/* Summary Cards */}
                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                    <div className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50">
                                                        <div className="text-center">
                                                            <div className="text-2xl font-bold text-blue-600">
                                                                {pricingHistory.length}
                                                            </div>
                                                            <div className="text-sm text-gray-600">Total Records</div>
                                                        </div>
                                                    </div>

                                                    <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50">
                                                        <div className="text-center">
                                                            <div className="text-2xl font-bold text-green-600">
                                                                {(() => {
                                                                    const currentPrice = pricingHistory.length > 0 ? (Number(pricingHistory[0].finalSpWithTax) || 0) : 0;
                                                                    return `₹${currentPrice.toFixed(2)}`;
                                                                })()}
                                                            </div>
                                                            <div className="text-sm text-gray-600">Current Price</div>
                                                        </div>
                                                    </div>                                        <div className="p-4 rounded-lg border-2 border-purple-200 bg-purple-50">
                                                        <div className="text-center">
                                                            <div className="text-xl font-bold text-purple-600">
                                                                {pricingHistory.length > 0 ?
                                                                    1 : 0} {/* Since API doesn't have pricingRule field */}
                                                            </div>
                                                            <div className="text-sm text-gray-600">Unique Rules</div>
                                                        </div>
                                                    </div>

                                                    <div className="p-4 rounded-lg border-2 border-orange-200 bg-orange-50">
                                                        <div className="text-center">
                                                            <div className="text-xl font-bold text-orange-600">
                                                                {pricingHistory.length > 0 ?
                                                                    Math.round((new Date().getTime() - new Date(pricingHistory[pricingHistory.length - 1].createdAt).getTime()) / (1000 * 60 * 60 * 24))
                                                                    : 0}
                                                            </div>
                                                            <div className="text-sm text-gray-600">Days Tracked</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Price Trends Chart */}
                                                <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                                                    <div className="flex items-center justify-between mb-6">
                                                        <div>
                                                            <h4 className="text-lg font-semibold text-gray-800">Price Trends Over Time</h4>
                                                            <p className="text-sm text-gray-500 mt-1">Hover over data points to see details</p>
                                                        </div>
                                                        <div className="flex items-center space-x-4 text-sm">
                                                            <div className="flex items-center">
                                                                <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                                                                <span className="text-gray-600">Final Price</span>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <div className="w-3 h-3 bg-red-500 rounded-full mr-2 opacity-70"></div>
                                                                <span className="text-gray-600">Old Price</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="relative h-80 w-full bg-gradient-to-br from-gray-50 to-white rounded-lg border">
                                                        {pricingHistory.length > 1 ? (
                                                            <svg
                                                                className="w-full h-full"
                                                                viewBox="0 0 900 300"
                                                                onMouseLeave={() => setChartTooltip({ show: false, x: 0, y: 0, data: null })}
                                                            >
                                                                {/* Grid Lines */}
                                                                <defs>
                                                                    <linearGradient id="blueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                                                        <stop offset="0%" style={{ stopColor: "#3B82F6", stopOpacity: 0.2 }} />
                                                                        <stop offset="100%" style={{ stopColor: "#3B82F6", stopOpacity: 0.02 }} />
                                                                    </linearGradient>
                                                                    <linearGradient id="redGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                                                        <stop offset="0%" style={{ stopColor: "#EF4444", stopOpacity: 0.1 }} />
                                                                        <stop offset="100%" style={{ stopColor: "#EF4444", stopOpacity: 0.02 }} />
                                                                    </linearGradient>
                                                                    <filter id="dropshadow">
                                                                        <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
                                                                        <feOffset dx="0" dy="2" result="offset" />
                                                                        <feComponentTransfer>
                                                                            <feFuncA type="linear" slope="0.2" />
                                                                        </feComponentTransfer>
                                                                        <feMerge>
                                                                            <feMergeNode />
                                                                            <feMergeNode in="SourceGraphic" />
                                                                        </feMerge>
                                                                    </filter>
                                                                </defs>

                                                                {/* Grid */}
                                                                {[...Array(6)].map((_, i) => (
                                                                    <g key={i}>
                                                                        <line
                                                                            x1="60"
                                                                            y1={40 + (i * 40)}
                                                                            x2="840"
                                                                            y2={40 + (i * 40)}
                                                                            stroke="#F3F4F6"
                                                                            strokeWidth="1"
                                                                        />
                                                                    </g>
                                                                ))}
                                                                {[...Array(8)].map((_, i) => (
                                                                    <line
                                                                        key={i}
                                                                        x1={60 + (i * 110)}
                                                                        y1="40"
                                                                        x2={60 + (i * 110)}
                                                                        y2="240"
                                                                        stroke="#F9FAFB"
                                                                        strokeWidth="1"
                                                                    />
                                                                ))}

                                                                {/* Y-Axis Labels */}
                                                                {(() => {
                                                                    const prices = pricingHistory.map(p => {
                                                                        const final = Number(p.finalSpWithTax) || 0;
                                                                        const old = Number(p.oldFinalSpWithTax) || 0;
                                                                        return [final, old];
                                                                    }).flat();
                                                                    const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                                                                    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                                                                    const range = maxPrice - minPrice;
                                                                    return [...Array(6)].map((_, i) => {
                                                                        const value = maxPrice - (i * range / 5);
                                                                        return (
                                                                            <text
                                                                                key={i}
                                                                                x="50"
                                                                                y={45 + (i * 40)}
                                                                                fontSize="11"
                                                                                fill="#6B7280"
                                                                                textAnchor="end"
                                                                                className="font-medium"
                                                                            >
                                                                                ₹{Math.round(value)}
                                                                            </text>
                                                                        );
                                                                    });
                                                                })()}

                                                                {/* Area under Final Price Line */}
                                                                <path
                                                                    d={`M 60 240 ${pricingHistory.slice().reverse().map((item, index) => {
                                                                        const x = 60 + (index / Math.max(pricingHistory.length - 1, 1)) * 780;
                                                                        const prices = pricingHistory.map(p => [Number(p.finalSpWithTax) || 0, Number(p.oldFinalSpWithTax) || 0]).flat();
                                                                        const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                                                                        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                                                                        const finalPrice = Number(item.finalSpWithTax) || 0;
                                                                        const y = maxPrice > minPrice ? 240 - ((finalPrice - minPrice) / (maxPrice - minPrice)) * 200 : 240;
                                                                        return `L ${x} ${y}`;
                                                                    }).join(' ')} L 840 240 Z`}
                                                                    fill="url(#blueGradient)"
                                                                />

                                                                {/* Final Price Line */}
                                                                <path
                                                                    d={`M ${pricingHistory.slice().reverse().map((item, index) => {
                                                                        const x = 60 + (index / Math.max(pricingHistory.length - 1, 1)) * 780;
                                                                        const prices = pricingHistory.map(p => [Number(p.finalSpWithTax) || 0, Number(p.oldFinalSpWithTax) || 0]).flat();
                                                                        const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                                                                        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                                                                        const finalPrice = Number(item.finalSpWithTax) || 0;
                                                                        const y = maxPrice > minPrice ? 240 - ((finalPrice - minPrice) / (maxPrice - minPrice)) * 200 : 240;
                                                                        return index === 0 ? `${x} ${y}` : `L ${x} ${y}`;
                                                                    }).join(' ')}`}
                                                                    fill="none"
                                                                    stroke="#3B82F6"
                                                                    strokeWidth="3"
                                                                    filter="url(#dropshadow)"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />

                                                                {/* Old Price Line */}
                                                                <path
                                                                    d={`M ${pricingHistory.slice().reverse().map((item, index) => {
                                                                        const x = 60 + (index / Math.max(pricingHistory.length - 1, 1)) * 780;
                                                                        const prices = pricingHistory.map(p => [Number(p.finalSpWithTax) || 0, Number(p.oldFinalSpWithTax) || 0]).flat();
                                                                        const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                                                                        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                                                                        const oldPrice = Number(item.oldFinalSpWithTax) || 0;
                                                                        const y = maxPrice > minPrice ? 240 - ((oldPrice - minPrice) / (maxPrice - minPrice)) * 200 : 240;
                                                                        return index === 0 ? `${x} ${y}` : `L ${x} ${y}`;
                                                                    }).join(' ')}`}
                                                                    fill="none"
                                                                    stroke="#EF4444"
                                                                    strokeWidth="2"
                                                                    strokeDasharray="8,4"
                                                                    opacity="0.7"
                                                                    strokeLinecap="round"
                                                                />

                                                                {/* Data Points */}
                                                                {pricingHistory.slice().reverse().map((item, index) => {
                                                                    const x = 60 + (index / Math.max(pricingHistory.length - 1, 1)) * 780;
                                                                    const prices = pricingHistory.map(p => [Number(p.finalSpWithTax) || 0, Number(p.oldFinalSpWithTax) || 0]).flat();
                                                                    const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                                                                    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                                                                    const finalPrice = Number(item.finalSpWithTax) || 0;
                                                                    const oldPrice = Number(item.oldFinalSpWithTax) || 0;
                                                                    const finalY = maxPrice > minPrice ? 240 - ((finalPrice - minPrice) / (maxPrice - minPrice)) * 200 : 240;
                                                                    const oldY = maxPrice > minPrice ? 240 - ((oldPrice - minPrice) / (maxPrice - minPrice)) * 200 : 240;

                                                                    return (
                                                                        <g key={item.id}>
                                                                            {/* Final Price Point */}
                                                                            <circle
                                                                                cx={x}
                                                                                cy={finalY}
                                                                                r="4"
                                                                                fill="#3B82F6"
                                                                                stroke="white"
                                                                                strokeWidth="2"
                                                                                className="cursor-pointer hover:r-6 transition-all duration-200"
                                                                                filter="url(#dropshadow)"
                                                                                onMouseEnter={(e) => {
                                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                                    setChartTooltip({
                                                                                        show: true,
                                                                                        x: e.clientX,
                                                                                        y: e.clientY - 10,
                                                                                        data: {
                                                                                            ...item,
                                                                                            type: 'final',
                                                                                            price: finalPrice,
                                                                                            date: new Date(item.createdAt).toLocaleDateString()
                                                                                        }
                                                                                    });
                                                                                }}
                                                                            />

                                                                            {/* Old Price Point */}
                                                                            <circle
                                                                                cx={x}
                                                                                cy={oldY}
                                                                                r="3"
                                                                                fill="#EF4444"
                                                                                stroke="white"
                                                                                strokeWidth="2"
                                                                                opacity="0.8"
                                                                                className="cursor-pointer hover:r-5 transition-all duration-200"
                                                                                onMouseEnter={(e) => {
                                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                                    setChartTooltip({
                                                                                        show: true,
                                                                                        x: e.clientX,
                                                                                        y: e.clientY - 10,
                                                                                        data: {
                                                                                            ...item,
                                                                                            type: 'old',
                                                                                            price: oldPrice,
                                                                                            date: new Date(item.createdAt).toLocaleDateString()
                                                                                        }
                                                                                    });
                                                                                }}
                                                                            />

                                                                            {/* Invisible hover area for better UX */}
                                                                            <rect
                                                                                x={x - 15}
                                                                                y="40"
                                                                                width="30"
                                                                                height="200"
                                                                                fill="transparent"
                                                                                className="cursor-pointer"
                                                                                onMouseEnter={(e) => {
                                                                                    setChartTooltip({
                                                                                        show: true,
                                                                                        x: e.clientX,
                                                                                        y: e.clientY - 10,
                                                                                        data: {
                                                                                            ...item,
                                                                                            type: 'both',
                                                                                            price: Number(item.finalSpWithTax) || 0,
                                                                                            oldPrice: Number(item.oldFinalSpWithTax) || 0,
                                                                                            date: new Date(item.createdAt).toLocaleDateString(),
                                                                                            time: new Date(item.createdAt).toLocaleTimeString()
                                                                                        }
                                                                                    });
                                                                                }}
                                                                            />
                                                                        </g>
                                                                    );
                                                                })}

                                                                {/* X-Axis */}
                                                                <line x1="60" y1="240" x2="840" y2="240" stroke="#D1D5DB" strokeWidth="2" />

                                                                {/* Y-Axis */}
                                                                <line x1="60" y1="40" x2="60" y2="240" stroke="#D1D5DB" strokeWidth="2" />

                                                                {/* X-Axis Labels */}
                                                                <text x="60" y="265" fontSize="11" fill="#6B7280" textAnchor="start" className="font-medium">
                                                                    {pricingHistory.length > 0 ? new Date(pricingHistory[pricingHistory.length - 1].createdAt).toLocaleDateString() : 'Start'}
                                                                </text>
                                                                <text x="840" y="265" fontSize="11" fill="#6B7280" textAnchor="end" className="font-medium">
                                                                    {pricingHistory.length > 0 ? new Date(pricingHistory[0].createdAt).toLocaleDateString() : 'Latest'}
                                                                </text>

                                                                {/* Chart Title on Axis */}
                                                                <text x="450" y="290" fontSize="12" fill="#374151" textAnchor="middle" className="font-semibold">
                                                                    Timeline →
                                                                </text>

                                                                {/* Y-Axis Label */}
                                                                <text x="25" y="140" fontSize="12" fill="#374151" textAnchor="middle" transform="rotate(-90 25 140)" className="font-semibold">
                                                                    Price (₹) →
                                                                </text>
                                                            </svg>
                                                        ) : (
                                                            <div className="flex items-center justify-center h-full text-gray-500">
                                                                <div className="text-center">
                                                                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                                    </svg>
                                                                    <p className="font-medium">No pricing data available</p>
                                                                    <p className="text-sm">Need at least 2 data points to show trends</p>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Professional Tooltip */}
                                                        {chartTooltip.show && chartTooltip.data && (
                                                            <div
                                                                className="fixed z-50 pointer-events-none"
                                                                style={{
                                                                    left: chartTooltip.x + 10,
                                                                    top: chartTooltip.y - 10,
                                                                    transform: 'translateY(-100%)'
                                                                }}
                                                            >
                                                                <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-4 max-w-xs">
                                                                    <div className="flex items-center justify-between mb-2">
                                                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                                            {chartTooltip.data.type === 'final' ? 'Final Price' :
                                                                                chartTooltip.data.type === 'old' ? 'Previous Price' : 'Price Change'}
                                                                        </span>
                                                                        <span className="text-xs text-gray-400">#{chartTooltip.data.id}</span>
                                                                    </div>

                                                                    <div className="space-y-2">
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-sm font-medium text-gray-700">Date:</span>
                                                                            <span className="text-sm text-gray-900">{chartTooltip.data.date}</span>
                                                                        </div>

                                                                        {chartTooltip.data.type === 'both' ? (
                                                                            <>
                                                                                <div className="flex items-center justify-between">
                                                                                    <span className="text-sm font-medium text-blue-600">Final Price:</span>
                                                                                    <span className="text-sm font-bold text-blue-700">₹{chartTooltip.data.price}</span>
                                                                                </div>
                                                                                <div className="flex items-center justify-between">
                                                                                    <span className="text-sm font-medium text-red-600">Old Price:</span>
                                                                                    <span className="text-sm font-bold text-red-700">₹{chartTooltip.data.oldPrice}</span>
                                                                                </div>
                                                                                <div className="flex items-center justify-between pt-1 border-t">
                                                                                    <span className="text-xs font-medium text-gray-600">Change:</span>
                                                                                    <span className={`text-xs font-bold ${chartTooltip.data.price > chartTooltip.data.oldPrice ? 'text-red-600' :
                                                                                        chartTooltip.data.price < chartTooltip.data.oldPrice ? 'text-green-600' : 'text-gray-600'
                                                                                        }`}>
                                                                                        {chartTooltip.data.price > chartTooltip.data.oldPrice ? '+' : ''}
                                                                                        ₹{(chartTooltip.data.price - chartTooltip.data.oldPrice).toFixed(2)}
                                                                                    </span>
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <div className="flex items-center justify-between">
                                                                                <span className="text-sm font-medium text-gray-700">Price:</span>
                                                                                <span className={`text-lg font-bold ${chartTooltip.data.type === 'final' ? 'text-blue-600' : 'text-red-600'
                                                                                    }`}>
                                                                                    ₹{chartTooltip.data.price}
                                                                                </span>
                                                                            </div>
                                                                        )}

                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-sm font-medium text-gray-700">Rule:</span>
                                                                            <span className="text-sm bg-gray-100 px-2 py-1 rounded text-gray-800 capitalize">
                                                                                Manual Update
                                                                            </span>
                                                                        </div>

                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-sm font-medium text-gray-700">By:</span>
                                                                            <span className="text-sm text-gray-900">{chartTooltip.data.updatedBy}</span>
                                                                        </div>

                                                                        {chartTooltip.data.time && (
                                                                            <div className="flex items-center justify-between">
                                                                                <span className="text-xs font-medium text-gray-500">Time:</span>
                                                                                <span className="text-xs text-gray-600">{chartTooltip.data.time}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Tooltip Arrow */}
                                                                    <div className="absolute bottom-0 left-6 transform translate-y-full">
                                                                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                                                                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200 -mt-px"></div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Rule Analysis */}
                                                <div className="bg-gray-50 p-6 rounded-lg">
                                                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Pricing Rules Analysis</h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {Array.from(new Set(pricingHistory.map(item => item.pricingRule))).map(rule => {
                                                            const ruleCount = pricingHistory.filter(item => item.pricingRule === rule).length;
                                                            const percentage = ((ruleCount / pricingHistory.length) * 100).toFixed(1);
                                                            return (
                                                                <div key={rule} className="bg-white p-4 rounded border">
                                                                    <div className="flex items-center justify-between mb-2">
                                                                        <span className="font-medium text-gray-800 capitalize">{rule}</span>
                                                                        <span className="text-sm text-gray-600">{percentage}%</span>
                                                                    </div>
                                                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                                                        <div
                                                                            className="bg-blue-500 h-2 rounded-full"
                                                                            style={{ width: `${percentage}%` }}
                                                                        ></div>
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 mt-1">{ruleCount} changes</div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Recent Changes Table */}
                                                <div className="bg-white border rounded-lg">
                                                    <div className="p-4 border-b">
                                                        <h4 className="text-lg font-semibold text-gray-800">Recent Price Changes</h4>
                                                        <p className="text-sm text-gray-500 mt-1">All available price change records ({pricingHistory.length} total)</p>
                                                    </div>
                                                    <div className="max-h-96 overflow-y-auto overflow-x-auto">
                                                        <table className="min-w-full divide-y divide-gray-200">
                                                            <thead className="bg-gray-50 sticky top-0">
                                                                <tr>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Old Price</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">New Price</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rule</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-200">
                                                                {pricingHistory.map((item, index) => {
                                                                    const finalPrice = Number(item.finalSpWithTax) || 0;
                                                                    const oldPrice = Number(item.oldFinalSpWithTax) || 0;
                                                                    const priceChange = finalPrice - oldPrice;
                                                                    const isIncrease = priceChange > 0;
                                                                    return (
                                                                        <tr key={item.id} className="hover:bg-gray-50">
                                                                            <td className="px-4 py-2 text-sm text-gray-900">
                                                                                <div>{new Date(item.createdAt).toLocaleDateString()}</div>
                                                                                <div className="text-xs text-gray-500">{new Date(item.createdAt).toLocaleTimeString()}</div>
                                                                            </td>
                                                                            <td className="px-4 py-2 text-sm text-gray-900">₹{oldPrice.toFixed(2)}</td>
                                                                            <td className="px-4 py-2 text-sm text-gray-900">₹{finalPrice.toFixed(2)}</td>
                                                                            <td className="px-4 py-2 text-sm">
                                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${isIncrease ? 'bg-red-100 text-red-800' :
                                                                                    priceChange < 0 ? 'bg-green-100 text-green-800' :
                                                                                        'bg-gray-100 text-gray-800'
                                                                                    }`}>
                                                                                    {priceChange > 0 ? '+' : ''}₹{priceChange.toFixed(2)}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-4 py-2 text-sm text-gray-900 capitalize">Manual Update</td>
                                                                            <td className="px-4 py-2 text-sm text-gray-900">{item.updatedBy}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                {/* Action Buttons */}
                                                <div className="flex justify-end space-x-3 pt-4 border-t">
                                                    <button
                                                        onClick={() => setShowPricingModal(false)}
                                                        className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                    >
                                                        Close
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Product Pricing History Modal */}
                        {showProductPricingHistoryModal && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                                <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
                                    <div className="mt-3">
                                        {/* Modal Header */}
                                        <div className="flex items-center justify-between pb-4 border-b">
                                            <h3 className="text-lg font-bold text-gray-900">
                                                Product Pricing History - {selectedProductMsn}
                                            </h3>
                                            <button
                                                onClick={() => setShowProductPricingHistoryModal(false)}
                                                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                            >
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>

                                        {productPricingHistoryLoading ? (
                                            <div className="flex justify-center items-center py-8">
                                                <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle>
                                                    <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" className="opacity-75"></path>
                                                </svg>
                                                <span className="ml-2">Loading product pricing history...</span>
                                            </div>
                                        ) : (
                                            <div className="mt-6 space-y-6 max-h-[70vh] overflow-y-auto">
                                                {/* Summary Cards */}
                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                    <div className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50">
                                                        <div className="text-center">
                                                            <div className="text-2xl font-bold text-blue-600">
                                                                {productPricingHistory.length}
                                                            </div>
                                                            <div className="text-sm text-gray-600">Total Records</div>
                                                        </div>
                                                    </div>

                                                    <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50">
                                                        <div className="text-center">
                                                            <div className="text-2xl font-bold text-green-600">
                                                                {(() => {
                                                                    const currentPrice = productPricingHistory.length > 0 ? (Number(productPricingHistory[0].finalSPWithTax) || 0) : 0;
                                                                    return `₹${currentPrice.toFixed(2)}`;
                                                                })()}
                                                            </div>
                                                            <div className="text-sm text-gray-600">Current Price</div>
                                                        </div>
                                                    </div>                                        <div className="p-4 rounded-lg border-2 border-purple-200 bg-purple-50">
                                                        <div className="text-center">
                                                            <div className="text-xl font-bold text-purple-600">
                                                                {productPricingHistory.length > 0 ?
                                                                    Array.from(new Set(productPricingHistory.map(item => item.pricingRule))).length : 0}
                                                            </div>
                                                            <div className="text-sm text-gray-600">Unique Rules</div>
                                                        </div>
                                                    </div>

                                                    <div className="p-4 rounded-lg border-2 border-orange-200 bg-orange-50">
                                                        <div className="text-center">
                                                            <div className="text-xl font-bold text-orange-600">
                                                                {productPricingHistory.length > 0 ?
                                                                    Math.round((new Date().getTime() - new Date(productPricingHistory[productPricingHistory.length - 1].createdAt).getTime()) / (1000 * 60 * 60 * 24))
                                                                    : 0}
                                                            </div>
                                                            <div className="text-sm text-gray-600">Days Tracked</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Price Trends Chart */}
                                                <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                                                    <div className="flex items-center justify-between mb-6">
                                                        <div>
                                                            <h4 className="text-lg font-semibold text-gray-800">Price Trends Over Time</h4>
                                                            <p className="text-sm text-gray-500 mt-1">Hover over data points to see details</p>
                                                        </div>
                                                        <div className="flex items-center space-x-4 text-sm">
                                                            <div className="flex items-center">
                                                                <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                                                                <span className="text-gray-600">Final Price</span>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <div className="w-3 h-3 bg-red-500 rounded-full mr-2 opacity-70"></div>
                                                                <span className="text-gray-600">Old Price</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="relative h-80 w-full bg-gradient-to-br from-gray-50 to-white rounded-lg border">
                                                        {productPricingHistory.length > 1 ? (
                                                            <svg
                                                                className="w-full h-full"
                                                                viewBox="0 0 900 300"
                                                                onMouseLeave={() => setChartTooltip({ show: false, x: 0, y: 0, data: null })}
                                                            >
                                                                {/* Grid Lines */}
                                                                <defs>
                                                                    <linearGradient id="blueGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
                                                                        <stop offset="0%" style={{ stopColor: "#3B82F6", stopOpacity: 0.2 }} />
                                                                        <stop offset="100%" style={{ stopColor: "#3B82F6", stopOpacity: 0.02 }} />
                                                                    </linearGradient>
                                                                    <linearGradient id="redGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
                                                                        <stop offset="0%" style={{ stopColor: "#EF4444", stopOpacity: 0.1 }} />
                                                                        <stop offset="100%" style={{ stopColor: "#EF4444", stopOpacity: 0.02 }} />
                                                                    </linearGradient>
                                                                    <filter id="dropshadow2">
                                                                        <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
                                                                        <feOffset dx="0" dy="2" result="offset" />
                                                                        <feComponentTransfer>
                                                                            <feFuncA type="linear" slope="0.2" />
                                                                        </feComponentTransfer>
                                                                        <feMerge>
                                                                            <feMergeNode />
                                                                            <feMergeNode in="SourceGraphic" />
                                                                        </feMerge>
                                                                    </filter>
                                                                </defs>

                                                                {/* Grid */}
                                                                {[...Array(6)].map((_, i) => (
                                                                    <g key={i}>
                                                                        <line
                                                                            x1="60"
                                                                            y1={40 + (i * 40)}
                                                                            x2="840"
                                                                            y2={40 + (i * 40)}
                                                                            stroke="#F3F4F6"
                                                                            strokeWidth="1"
                                                                        />
                                                                    </g>
                                                                ))}
                                                                {[...Array(8)].map((_, i) => (
                                                                    <line
                                                                        key={i}
                                                                        x1={60 + (i * 110)}
                                                                        y1="40"
                                                                        x2={60 + (i * 110)}
                                                                        y2="240"
                                                                        stroke="#F9FAFB"
                                                                        strokeWidth="1"
                                                                    />
                                                                ))}

                                                                {/* Y-Axis Labels */}
                                                                {(() => {
                                                                    const prices = productPricingHistory.map(p => {
                                                                        const final = Number(p.finalSPWithTax) || 0;
                                                                        const old = Number(p.oldFinalSPWithTax) || 0;
                                                                        return [final, old];
                                                                    }).flat();
                                                                    const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                                                                    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                                                                    const range = maxPrice - minPrice;
                                                                    return [...Array(6)].map((_, i) => {
                                                                        const value = maxPrice - (i * range / 5);
                                                                        return (
                                                                            <text
                                                                                key={i}
                                                                                x="50"
                                                                                y={45 + (i * 40)}
                                                                                fontSize="11"
                                                                                fill="#6B7280"
                                                                                textAnchor="end"
                                                                                className="font-medium"
                                                                            >
                                                                                ₹{Math.round(value)}
                                                                            </text>
                                                                        );
                                                                    });
                                                                })()}

                                                                {/* Area under Final Price Line */}
                                                                <path
                                                                    d={`M 60 240 ${productPricingHistory.slice().reverse().map((item, index) => {
                                                                        const x = 60 + (index / Math.max(productPricingHistory.length - 1, 1)) * 780;
                                                                        const prices = productPricingHistory.map(p => [Number(p.finalSPWithTax) || 0, Number(p.oldFinalSPWithTax) || 0]).flat();
                                                                        const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                                                                        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                                                                        const finalPrice = Number(item.finalSPWithTax) || 0;
                                                                        const y = maxPrice > minPrice ? 240 - ((finalPrice - minPrice) / (maxPrice - minPrice)) * 200 : 240;
                                                                        return `L ${x} ${y}`;
                                                                    }).join(' ')} L 840 240 Z`}
                                                                    fill="url(#blueGradient2)"
                                                                />

                                                                {/* Final Price Line */}
                                                                <path
                                                                    d={`M ${productPricingHistory.slice().reverse().map((item, index) => {
                                                                        const x = 60 + (index / Math.max(productPricingHistory.length - 1, 1)) * 780;
                                                                        const prices = productPricingHistory.map(p => [Number(p.finalSPWithTax) || 0, Number(p.oldFinalSPWithTax) || 0]).flat();
                                                                        const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                                                                        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                                                                        const finalPrice = Number(item.finalSPWithTax) || 0;
                                                                        const y = maxPrice > minPrice ? 240 - ((finalPrice - minPrice) / (maxPrice - minPrice)) * 200 : 240;
                                                                        return index === 0 ? `${x} ${y}` : `L ${x} ${y}`;
                                                                    }).join(' ')}`}
                                                                    fill="none"
                                                                    stroke="#3B82F6"
                                                                    strokeWidth="3"
                                                                    filter="url(#dropshadow2)"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />

                                                                {/* Old Price Line */}
                                                                <path
                                                                    d={`M ${productPricingHistory.slice().reverse().map((item, index) => {
                                                                        const x = 60 + (index / Math.max(productPricingHistory.length - 1, 1)) * 780;
                                                                        const prices = productPricingHistory.map(p => [Number(p.finalSPWithTax) || 0, Number(p.oldFinalSPWithTax) || 0]).flat();
                                                                        const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                                                                        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                                                                        const oldPrice = Number(item.oldFinalSPWithTax) || 0;
                                                                        const y = maxPrice > minPrice ? 240 - ((oldPrice - minPrice) / (maxPrice - minPrice)) * 200 : 240;
                                                                        return index === 0 ? `${x} ${y}` : `L ${x} ${y}`;
                                                                    }).join(' ')}`}
                                                                    fill="none"
                                                                    stroke="#EF4444"
                                                                    strokeWidth="2"
                                                                    strokeDasharray="8,4"
                                                                    opacity="0.7"
                                                                    strokeLinecap="round"
                                                                />

                                                                {/* Data Points */}
                                                                {productPricingHistory.slice().reverse().map((item, index) => {
                                                                    const x = 60 + (index / Math.max(productPricingHistory.length - 1, 1)) * 780;
                                                                    const prices = productPricingHistory.map(p => [Number(p.finalSPWithTax) || 0, Number(p.oldFinalSPWithTax) || 0]).flat();
                                                                    const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                                                                    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                                                                    const finalPrice = Number(item.finalSPWithTax) || 0;
                                                                    const oldPrice = Number(item.oldFinalSPWithTax) || 0;
                                                                    const finalY = maxPrice > minPrice ? 240 - ((finalPrice - minPrice) / (maxPrice - minPrice)) * 200 : 240;
                                                                    const oldY = maxPrice > minPrice ? 240 - ((oldPrice - minPrice) / (maxPrice - minPrice)) * 200 : 240;

                                                                    return (
                                                                        <g key={item.id}>
                                                                            {/* Final Price Point */}
                                                                            <circle
                                                                                cx={x}
                                                                                cy={finalY}
                                                                                r="4"
                                                                                fill="#3B82F6"
                                                                                stroke="white"
                                                                                strokeWidth="2"
                                                                                className="cursor-pointer hover:r-6 transition-all duration-200"
                                                                                filter="url(#dropshadow2)"
                                                                                onMouseEnter={(e) => {
                                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                                    setChartTooltip({
                                                                                        show: true,
                                                                                        x: e.clientX,
                                                                                        y: e.clientY - 10,
                                                                                        data: {
                                                                                            ...item,
                                                                                            type: 'final',
                                                                                            price: finalPrice,
                                                                                            date: new Date(item.createdAt).toLocaleDateString()
                                                                                        }
                                                                                    });
                                                                                }}
                                                                            />

                                                                            {/* Old Price Point */}
                                                                            <circle
                                                                                cx={x}
                                                                                cy={oldY}
                                                                                r="3"
                                                                                fill="#EF4444"
                                                                                stroke="white"
                                                                                strokeWidth="2"
                                                                                opacity="0.8"
                                                                                className="cursor-pointer hover:r-5 transition-all duration-200"
                                                                                onMouseEnter={(e) => {
                                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                                    setChartTooltip({
                                                                                        show: true,
                                                                                        x: e.clientX,
                                                                                        y: e.clientY - 10,
                                                                                        data: {
                                                                                            ...item,
                                                                                            type: 'old',
                                                                                            price: oldPrice,
                                                                                            date: new Date(item.createdAt).toLocaleDateString()
                                                                                        }
                                                                                    });
                                                                                }}
                                                                            />

                                                                            {/* Invisible hover area for better UX */}
                                                                            <rect
                                                                                x={x - 15}
                                                                                y="40"
                                                                                width="30"
                                                                                height="200"
                                                                                fill="transparent"
                                                                                className="cursor-pointer"
                                                                                onMouseEnter={(e) => {
                                                                                    setChartTooltip({
                                                                                        show: true,
                                                                                        x: e.clientX,
                                                                                        y: e.clientY - 10,
                                                                                        data: {
                                                                                            ...item,
                                                                                            type: 'both',
                                                                                            price: Number(item.finalSPWithTax) || 0,
                                                                                            oldPrice: Number(item.oldFinalSPWithTax) || 0,
                                                                                            date: new Date(item.createdAt).toLocaleDateString(),
                                                                                            time: new Date(item.createdAt).toLocaleTimeString()
                                                                                        }
                                                                                    });
                                                                                }}
                                                                            />
                                                                        </g>
                                                                    );
                                                                })}

                                                                {/* X-Axis */}
                                                                <line x1="60" y1="240" x2="840" y2="240" stroke="#D1D5DB" strokeWidth="2" />

                                                                {/* Y-Axis */}
                                                                <line x1="60" y1="40" x2="60" y2="240" stroke="#D1D5DB" strokeWidth="2" />

                                                                {/* X-Axis Labels */}
                                                                <text x="60" y="265" fontSize="11" fill="#6B7280" textAnchor="start" className="font-medium">
                                                                    {productPricingHistory.length > 0 ? new Date(productPricingHistory[productPricingHistory.length - 1].createdAt).toLocaleDateString() : 'Start'}
                                                                </text>
                                                                <text x="840" y="265" fontSize="11" fill="#6B7280" textAnchor="end" className="font-medium">
                                                                    {productPricingHistory.length > 0 ? new Date(productPricingHistory[0].createdAt).toLocaleDateString() : 'Latest'}
                                                                </text>

                                                                {/* Chart Title on Axis */}
                                                                <text x="450" y="290" fontSize="12" fill="#374151" textAnchor="middle" className="font-semibold">
                                                                    Timeline →
                                                                </text>

                                                                {/* Y-Axis Label */}
                                                                <text x="25" y="140" fontSize="12" fill="#374151" textAnchor="middle" transform="rotate(-90 25 140)" className="font-semibold">
                                                                    Price (₹) →
                                                                </text>
                                                            </svg>
                                                        ) : (
                                                            <div className="flex items-center justify-center h-full text-gray-500">
                                                                <div className="text-center">
                                                                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                                    </svg>
                                                                    <p className="font-medium">No pricing data available</p>
                                                                    <p className="text-sm">Need at least 2 data points to show trends</p>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Professional Tooltip */}
                                                        {chartTooltip.show && chartTooltip.data && (
                                                            <div
                                                                className="fixed z-50 pointer-events-none"
                                                                style={{
                                                                    left: chartTooltip.x + 10,
                                                                    top: chartTooltip.y - 10,
                                                                    transform: 'translateY(-100%)'
                                                                }}
                                                            >
                                                                <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-4 max-w-xs">
                                                                    <div className="flex items-center justify-between mb-2">
                                                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                                            {chartTooltip.data.type === 'final' ? 'Final Price' :
                                                                                chartTooltip.data.type === 'old' ? 'Previous Price' : 'Price Change'}
                                                                        </span>
                                                                        <span className="text-xs text-gray-400">#{chartTooltip.data.id}</span>
                                                                    </div>

                                                                    <div className="space-y-2">
                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-sm font-medium text-gray-700">Date:</span>
                                                                            <span className="text-sm text-gray-900">{chartTooltip.data.date}</span>
                                                                        </div>

                                                                        {chartTooltip.data.type === 'both' ? (
                                                                            <>
                                                                                <div className="flex items-center justify-between">
                                                                                    <span className="text-sm font-medium text-blue-600">Final Price:</span>
                                                                                    <span className="text-sm font-bold text-blue-700">₹{chartTooltip.data.price}</span>
                                                                                </div>
                                                                                <div className="flex items-center justify-between">
                                                                                    <span className="text-sm font-medium text-red-600">Old Price:</span>
                                                                                    <span className="text-sm font-bold text-red-700">₹{chartTooltip.data.oldPrice}</span>
                                                                                </div>
                                                                                <div className="flex items-center justify-between pt-1 border-t">
                                                                                    <span className="text-xs font-medium text-gray-600">Change:</span>
                                                                                    <span className={`text-xs font-bold ${chartTooltip.data.price > chartTooltip.data.oldPrice ? 'text-red-600' :
                                                                                        chartTooltip.data.price < chartTooltip.data.oldPrice ? 'text-green-600' : 'text-gray-600'
                                                                                        }`}>
                                                                                        {chartTooltip.data.price > chartTooltip.data.oldPrice ? '+' : ''}
                                                                                        ₹{(chartTooltip.data.price - chartTooltip.data.oldPrice).toFixed(2)}
                                                                                    </span>
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <div className="flex items-center justify-between">
                                                                                <span className="text-sm font-medium text-gray-700">Price:</span>
                                                                                <span className={`text-lg font-bold ${chartTooltip.data.type === 'final' ? 'text-blue-600' : 'text-red-600'
                                                                                    }`}>
                                                                                    ₹{chartTooltip.data.price}
                                                                                </span>
                                                                            </div>
                                                                        )}

                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-sm font-medium text-gray-700">Rule:</span>
                                                                            <span className="text-sm bg-gray-100 px-2 py-1 rounded text-gray-800 capitalize">
                                                                                {chartTooltip.data.pricingRule || "Manual Update"}
                                                                            </span>
                                                                        </div>

                                                                        <div className="flex items-center justify-between">
                                                                            <span className="text-sm font-medium text-gray-700">By:</span>
                                                                            <span className="text-sm text-gray-900">{chartTooltip.data.createdBy || chartTooltip.data.updatedBy || "-"}</span>
                                                                        </div>

                                                                        {chartTooltip.data.time && (
                                                                            <div className="flex items-center justify-between">
                                                                                <span className="text-xs font-medium text-gray-500">Time:</span>
                                                                                <span className="text-xs text-gray-600">{chartTooltip.data.time}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Tooltip Arrow */}
                                                                    <div className="absolute bottom-0 left-6 transform translate-y-full">
                                                                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                                                                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200 -mt-px"></div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Rule Analysis */}
                                                <div className="bg-gray-50 p-6 rounded-lg">
                                                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Pricing Rules Analysis</h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {Array.from(new Set(productPricingHistory.map(item => item.pricingRule))).map(rule => {
                                                            const ruleCount = productPricingHistory.filter(item => item.pricingRule === rule).length;
                                                            const percentage = ((ruleCount / productPricingHistory.length) * 100).toFixed(1);
                                                            return (
                                                                <div key={rule} className="bg-white p-4 rounded border">
                                                                    <div className="flex items-center justify-between mb-2">
                                                                        <span className="font-medium text-gray-800 capitalize">{rule || "Manual Update"}</span>
                                                                        <span className="text-sm text-gray-600">{percentage}%</span>
                                                                    </div>
                                                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                                                        <div
                                                                            className="bg-blue-500 h-2 rounded-full"
                                                                            style={{ width: `${percentage}%` }}
                                                                        ></div>
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 mt-1">{ruleCount} changes</div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Recent Changes Table */}
                                                <div className="bg-white border rounded-lg">
                                                    <div className="p-4 border-b">
                                                        <h4 className="text-lg font-semibold text-gray-800">Recent Price Changes</h4>
                                                        <p className="text-sm text-gray-500 mt-1">All available price change records ({productPricingHistory.length} total)</p>
                                                    </div>
                                                    <div className="max-h-96 overflow-y-auto overflow-x-auto">
                                                        <table className="min-w-full divide-y divide-gray-200">
                                                            <thead className="bg-gray-50 sticky top-0">
                                                                <tr>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Old Price</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">New Price</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rule</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-200">
                                                                {productPricingHistory.map((item, index) => {
                                                                    const finalPrice = Number(item.finalSPWithTax) || 0;
                                                                    const oldPrice = Number(item.oldFinalSPWithTax) || 0;
                                                                    const priceChange = finalPrice - oldPrice;
                                                                    const isIncrease = priceChange > 0;
                                                                    return (
                                                                        <tr key={item.id} className="hover:bg-gray-50">
                                                                            <td className="px-4 py-2 text-sm text-gray-900">
                                                                                <div>{new Date(item.createdAt).toLocaleDateString()}</div>
                                                                                <div className="text-xs text-gray-500">{new Date(item.createdAt).toLocaleTimeString()}</div>
                                                                            </td>
                                                                            <td className="px-4 py-2 text-sm text-gray-900">₹{oldPrice.toFixed(2)}</td>
                                                                            <td className="px-4 py-2 text-sm text-gray-900">₹{finalPrice.toFixed(2)}</td>
                                                                            <td className="px-4 py-2 text-sm">
                                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${isIncrease ? 'bg-red-100 text-red-800' :
                                                                                    priceChange < 0 ? 'bg-green-100 text-green-800' :
                                                                                        'bg-gray-100 text-gray-800'
                                                                                    }`}>
                                                                                    {priceChange > 0 ? '+' : ''}₹{priceChange.toFixed(2)}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-4 py-2 text-sm text-gray-900 capitalize">{item.pricingRule || "Manual Update"}</td>
                                                                            <td className="px-4 py-2 text-sm text-gray-900">{item.createdBy || "-"}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                {/* Action Buttons */}
                                                <div className="flex justify-end space-x-3 pt-4 border-t">
                                                    <button
                                                        onClick={() => setShowProductPricingHistoryModal(false)}
                                                        className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                    >
                                                        Close
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}



                        {/* Product Pricing Upload Result Modal */}
                        {showProductPricingUploadModal && productPricingUploadResult && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                                <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                                    <div className="mt-3">
                                        <div className="flex items-center justify-between pb-4 border-b">
                                            <h3 className="text-lg font-bold text-gray-900">
                                                Product Pricing Upload Results
                                            </h3>
                                            <button
                                                onClick={() => setShowProductPricingUploadModal(false)}
                                                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                            >
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>

                                        <div className="mt-6 space-y-6">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className={`p-4 rounded-lg border-2 ${productPricingUploadResult.numSucceeded > 0 ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold text-green-600">
                                                            {productPricingUploadResult.numSucceeded || 0}
                                                        </div>
                                                        <div className="text-sm text-gray-600">Records Succeeded</div>
                                                    </div>
                                                </div>

                                                <div className={`p-4 rounded-lg border-2 ${productPricingUploadResult.numFailed > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold text-red-600">
                                                            {productPricingUploadResult.numFailed || 0}
                                                        </div>
                                                        <div className="text-sm text-gray-600">Records Failed</div>
                                                    </div>
                                                </div>

                                                <div className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50">
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold text-blue-600">
                                                            {((productPricingUploadResult.uploadFinish || 0) - (productPricingUploadResult.uploadStart || 0))}ms
                                                        </div>
                                                        <div className="text-sm text-gray-600">Processing Time</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <h4 className="text-md font-semibold text-gray-800 mb-3">Upload Details</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                    <div>
                                                        <span className="font-medium text-gray-600">Uploaded By:</span>
                                                        <span className="ml-2 text-gray-800">{productPricingUploadResult.uploadedBy || 'N/A'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-gray-600">File Name:</span>
                                                        <span className="ml-2 text-blue-600">
                                                            {productPricingUploadResult.s3FilePath ? (
                                                                <a href={productPricingUploadResult.s3FilePath} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                                                    {productPricingUploadResult.downloadFileName || 'Download File'}
                                                                </a>
                                                            ) : (
                                                                productPricingUploadResult.downloadFileName || 'N/A'
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {productPricingUploadResult.failureDetails && Object.keys(productPricingUploadResult.failureDetails).length > 0 && (
                                                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                                                    <h4 className="text-md font-semibold text-red-800 mb-3">Failure Details</h4>
                                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                                        {Object.entries(productPricingUploadResult.failureDetails).map(([rowNumber, errors]) => (
                                                            <div key={rowNumber} className="bg-white p-3 rounded border border-red-100">
                                                                <div className="font-medium text-red-700 mb-1">Row {rowNumber}:</div>
                                                                <div className="text-sm text-red-600">
                                                                    {Array.isArray(errors) ? (
                                                                        <ul className="list-disc list-inside space-y-1">
                                                                            {errors.map((error: any, index: number) => (
                                                                                <li key={index}>{error}</li>
                                                                            ))}
                                                                        </ul>
                                                                    ) : (
                                                                        <div>{String(errors)}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                                <button
                                                    onClick={() => setShowProductPricingUploadModal(false)}
                                                    className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Upload Result Modal */}
                        {showUploadModal && uploadResult && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                                <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                                    <div className="mt-3">
                                        {/* Modal Header */}
                                        <div className="flex items-center justify-between pb-4 border-b">
                                            <h3 className="text-lg font-bold text-gray-900">
                                                NDD Offer Price Upload Results
                                            </h3>
                                            <button
                                                onClick={() => setShowUploadModal(false)}
                                                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                            >
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>

                                        {/* Modal Content */}
                                        <div className="mt-6 space-y-6">
                                            {/* Status Overview */}
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className={`p-4 rounded-lg border-2 ${uploadResult.data?.numSucceeded > 0 ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold text-green-600">
                                                            {uploadResult.data?.numSucceeded || 0}
                                                        </div>
                                                        <div className="text-sm text-gray-600">Records Succeeded</div>
                                                    </div>
                                                </div>

                                                <div className={`p-4 rounded-lg border-2 ${uploadResult.data?.numFailed > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold text-red-600">
                                                            {uploadResult.data?.numFailed || 0}
                                                        </div>
                                                        <div className="text-sm text-gray-600">Records Failed</div>
                                                    </div>
                                                </div>

                                                <div className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50">
                                                    <div className="text-center">
                                                        <div className="text-2xl font-bold text-blue-600">
                                                            {((uploadResult.data?.uploadFinish || 0) - (uploadResult.data?.uploadStart || 0))}ms
                                                        </div>
                                                        <div className="text-sm text-gray-600">Processing Time</div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Upload Details */}
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <h4 className="text-md font-semibold text-gray-800 mb-3">Upload Details</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                    <div>
                                                        <span className="font-medium text-gray-600">Uploaded By:</span>
                                                        <span className="ml-2 text-gray-800">{uploadResult.data?.uploadedBy || 'N/A'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-gray-600">Upload Type:</span>
                                                        <span className="ml-2 text-gray-800">{uploadResult.data?.uploadedFor || 'N/A'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-gray-600">File Name:</span>
                                                        <span className="ml-2 text-gray-800">{uploadResult.data?.downloadFileName || 'N/A'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-gray-600">Status:</span>
                                                        <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${uploadResult.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                            }`}>
                                                            {uploadResult.status ? 'Success' : 'Failed'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Failure Details */}
                                            {uploadResult.data?.failureDetails && Object.keys(uploadResult.data.failureDetails).length > 0 && (
                                                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                                                    <h4 className="text-md font-semibold text-red-800 mb-3">Failure Details</h4>
                                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                                        {Object.entries(uploadResult.data.failureDetails).map(([rowNumber, errors]) => (
                                                            <div key={rowNumber} className="bg-white p-3 rounded border border-red-100">
                                                                <div className="font-medium text-red-700 mb-1">Row {rowNumber}:</div>
                                                                <div className="text-sm text-red-600">
                                                                    {Array.isArray(errors) ? (
                                                                        <ul className="list-disc list-inside space-y-1">
                                                                            {errors.map((error, index) => (
                                                                                <li key={index}>{error}</li>
                                                                            ))}
                                                                        </ul>
                                                                    ) : (
                                                                        <div>{String(errors)}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                                <button
                                                    onClick={() => setShowUploadModal(false)}
                                                    className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductNddSupplier;
